"use client";

import { useRef, useCallback, useMemo } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { VisuallyHidden } from "radix-ui";
import { Button } from "@/components/ui/button";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { useAuth } from "@/features/auth";
import { useQuerySearchParams } from "@/hooks/use-query-search-params";
import { parseAsString } from "nuqs";

const WelcomeVideoPlayer = dynamic(
  () =>
    import("./welcome-video-player").then((mod) => ({
      default: mod.WelcomeVideoPlayer,
    })),
  { ssr: false },
);

const GETTING_STARTED = [
  {
    icon: "/assets/welcome/ai-search-icon.svg",
    title: "Smart AI Filters",
    description:
      "Define your key requirements such as role, skills, and experience level.",
  },
  {
    icon: "/assets/welcome/ai-sheets-icon.svg",
    title: "Arbitrator",
    description:
      "Let Arbi automatically surface top matches based on your criteria.",
  },
];

const EXPLORE_MORE = [
  "Click Summarize to view instant AI-generated profile insights.",
  "Add AI prompts to your outreach templates for personalized engagement.",
  "Open Insights to analyze and compare your talent pool.",
];

export function WelcomeModal() {
  const { user } = useAuth();
  const userName = user?.first_name || "";
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [welcome, setWelcome] = useQuerySearchParams({
    field: "welcome",
    parser: parseAsString.withDefault(""),
  });
  const open = useMemo(() => welcome === "true", [welcome]);

  const handleWatchDemo = useCallback(() => {
    videoRef.current?.play();
  }, []);

  const onDismiss = useCallback(() => {
    setWelcome(null, { history: "replace", scroll: false });
  }, [setWelcome]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onDismiss();
    },
    [onDismiss],
  );

  const welcomeTitle = userName
    ? `Welcome to Arbi, ${userName}`
    : "Welcome to Arbi";

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent size="xl" className="max-h-[92dvh] md:max-w-[1180px]">
        <VisuallyHidden.Root>
          <ModalTitle>{welcomeTitle}</ModalTitle>
        </VisuallyHidden.Root>

        <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-5 sm:flex-row sm:items-start sm:gap-6 sm:p-6 lg:gap-12 lg:p-9">
          {/* ───── Left Panel ───── */}
          <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-1 lg:w-[571px] lg:flex-none">
            <h2 className="text-2xl font-semibold text-heading">
              Welcome to Arbi, {userName || "there"} 🎉
            </h2>

            <div className="flex flex-col gap-[15px]">
              {/* Intro text */}
              <div className="flex flex-col gap-0">
                <p className="text-base text-ai">
                  Your Arbi account is now active.
                </p>
                <br />
                <p className="text-base text-heading">
                  Arbi helps you streamline profile discovery with AI-powered
                  search, ranking, screening and automation. It&apos;s not just
                  faster sourcing; it&apos;s a smarter way to build your
                  pipeline.
                </p>
                <br />
                <p className="text-lg font-medium text-heading">
                  To get started:
                </p>
              </div>

              {/* Feature cards — Smart AI Filters + Arbitrator */}
              <div className="flex gap-3">
                {GETTING_STARTED.map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-1 flex-col gap-1.5 rounded-lg border border-border bg-contrast/3 p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                  >
                    <div className="flex items-center gap-1.5">
                      <Image
                        src={item.icon}
                        alt=""
                        width={16}
                        height={16}
                        className="size-4 shrink-0"
                      />
                      <span className="welcome-feature-title text-sm font-medium bg-clip-text text-transparent">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-[13px] text-contrast/70 leading-normal">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* Explore more */}
              <div className="flex flex-col gap-2">
                <p className="text-lg font-medium text-heading">
                  Explore more as you go:
                </p>
                <div className="flex flex-col gap-2 lg:flex-row lg:gap-1.5">
                  {EXPLORE_MORE.map((text, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-lg border border-border bg-contrast/3 p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                    >
                      <p className="text-[13px] font-medium text-heading leading-normal">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ───── Right Panel ───── */}
          <div className="flex min-w-0 z-2 flex-col self-stretch sm:flex-1 lg:justify-between">
            {/* Video player */}
            <div className="aspect-video overflow-hidden">
              <WelcomeVideoPlayer ref={videoRef} />
            </div>

            {/* Support text */}
            <p className="mt-4 text-sm text-contrast/80">
              Feel free to reach out with any questions! You can chat with us
              directly or drop us an email at support@neuroscale.ai.
            </p>

            {/* CTA buttons */}
            <div className="mt-4 hidden flex-col gap-3 sm:flex">
              <Button
                onClick={handleWatchDemo}
                variant="primary"
                className="w-full"
              >
                Watch the demo
                <Image
                  src="/assets/welcome/play-icon.svg"
                  alt=""
                  width={16}
                  height={16}
                  className="size-4"
                />
              </Button>

              <Button
                variant="ghost"
                onClick={onDismiss}
                className="h-auto w-full py-2 text-sm font-medium text-contrast/60 hover:text-contrast/80 hover:opacity-100"
              >
                Continue to Arbi
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile-only pinned CTA footer */}
        <div className="relative flex shrink-0 flex-col gap-3 border-t border-border bg-surface-header px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] before:pointer-events-none before:absolute before:inset-x-0 before:-top-6 before:h-6 before:bg-gradient-to-t before:from-surface-elevated before:to-transparent sm:hidden">
          <Button
            onClick={handleWatchDemo}
            variant="primary"
            className="w-full"
          >
            Watch the demo
            <Image
              src="/assets/welcome/play-icon.svg"
              alt=""
              width={16}
              height={16}
              className="size-4"
            />
          </Button>

          <Button
            variant="ghost"
            onClick={onDismiss}
            className="h-auto w-full py-2 text-sm font-medium text-contrast/60 hover:text-contrast/80 hover:opacity-100"
          >
            Continue to Arbi
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
